import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Typography,
  TypographyVariant,
  Iconography,
  TextArea,
  Loader,
  LoaderSizes,
} from "@hid-galaxy-ui/galaxy-react";
import ChatMessage from "./ChatMessage";
import { IconographySizes } from "@hid-galaxy-ui/galaxy-react/components/Iconography/iconEnums";
import { getText } from "../__utils__/textUtils";
import TypingIndicator from "./TypingIndicator";
import { getAuthToken, getUserInfo } from "../utils";
import { useNavigate } from "react-router-dom";
import { LogoutPath } from "../routes";
import { API_BASE_URL, TENANT_ID } from "../utils/common";

// Polling configuration constants
const POLLING_MAX_ATTEMPTS = 25;
const POLLING_INITIAL_DELAY_MS = 500; // Delay for first 10 polling attempts (faster for quick responses)
const POLLING_EXTENDED_DELAY_MS = 1000; // Delay for remaining polling attempts (slower to reduce server load)
const POLLING_INITIAL_PHASE_COUNT = 10; // Number of attempts with initial delay

const Chatform = ({
  setChatStarted,
  selectedConversation,
  refreshSidenav,
}: {
  setChatStarted: any;
  selectedConversation: string | null;
  refreshSidenav: () => void;
}) => {
  const [messages, setMessages] = useState<any>([]);
  const [input, setInput] = useState("");
  const [userData, setUserData] = useState<any>(null);
  const [isdisabled, setIsDisabled] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [pendingQuery, setPendingQuery] = useState<string | null>(null);
  const hasSentFirstMessage = useRef(false);
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const [retryRequestId, setRetryRequestId] = useState<string | null>(null);
  const [retryUserMsgId, setRetryUserMsgId] = useState<string | null>(null);
  const [retryConversationId, setRetryConversationId] = useState<string | null>(
    null
  );
  const [hasRefreshedForTitle, setHasRefreshedForTitle] =
    useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  useEffect(() => {
    abortControllerRef.current = new AbortController();
    const abortController = abortControllerRef.current;

    return () => {
      abortController.abort();
    };
  }, [selectedConversation]);
  useEffect(() => {
    setIsDisabled(false); // Reset button disabled state when switching chats
    setInput(""); // ✅ Clear user input
    setPendingQuery(null);
    setHasRefreshedForTitle(false);
    // Reset the 'first message' flag when starting a new chat
    if (selectedConversation === null) {
      hasSentFirstMessage.current = false;
    }
  }, [selectedConversation]);
  useEffect(() => {
    if (!selectedConversation) return;
    const fetchChatHistory = async () => {
      setChatLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/users/conversations/current/${selectedConversation}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
            "x-tenant-id": TENANT_ID || "",
          },
        }
      );
      if (response.status === 401) {
        navigate(LogoutPath);
        return;
      }
      if (!response.ok) throw new Error("Failed to fetch bot response");
       // Set userData when loading chat history
   const userInfo = getUserInfo();
    let userId = null;
    
    try {
      if (userInfo) {
        const parsedUserInfo = JSON.parse(userInfo);
        userId = parsedUserInfo?.sub || parsedUserInfo?.user_id || null;
      }
    } catch (error) {
      console.error("Error parsing user info:", error);
    }
     setUserData({
      conversation_session_id: selectedConversation,
      session_id: getSessionId(selectedConversation),
      user_id: userId, // Add this required field
    });
      const data = await response.json();
      const chatMessages = data?.message?.flatMap((msg: any) => {
        const feedbackType = msg?.feedback_type;
        const request_id = msg?.request_id;
        return [
          { text: msg.question?.[0]?.query, sender: "user" },
          {
            text: msg.generated_response?.[0]?.answer,
            sender: "bot",
            feedback_type: feedbackType,
            request_id: request_id,
            feedback_comment: "",
            sourceUrl: msg.generated_response?.[0]?.citations?.source_url || [],
          },
        ];
      });
      setMessages(chatMessages);
      setChatStarted(true);
      setChatLoading(false);
    };
    fetchChatHistory();
  }, [selectedConversation]);
  useEffect(() => {
    if (messages.length > 1) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const getSessionId = (selectedConversation: string | null): string => {
    if (!selectedConversation) return "";
    return selectedConversation.replace("C-", "");
  };
  const handleRetryFailure = (
    retryRequestId: string | null,
    retryUserMsgId: string | null,
    latestMessages: any[],
    conversationIdForRetry?: string | null
  ) => {
    const failedUserMsg = latestMessages.find(
      (msg: any) =>
        msg.sender === "user" &&
        (msg.request_id === retryRequestId || msg.id === retryUserMsgId)
    );
    setRetryRequestId(retryRequestId);
    setRetryUserMsgId(failedUserMsg?.id);
    if (conversationIdForRetry) {
      setRetryConversationId(conversationIdForRetry);
    }
    setMessages((prev: any) => prev.filter((msg: any) => msg.id !== "typing"));
  };

  const handleRetry = async () => {
    setRetryRequestId(null);
    setRetryUserMsgId(null);
    const conversationId =
      selectedConversation ||
      retryConversationId ||
      userData?.conversation_session_id;
    const signal = abortControllerRef.current?.signal;
    try {
      await fetch(`${API_BASE_URL}/retryQuery`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
          "x-tenant-id": TENANT_ID || "",
        },
        body: JSON.stringify({
          conversation_session_id: conversationId,
          request_id: retryRequestId,
        }),
        signal,
      });
      setMessages((prev: any) => [
        ...prev,
        { sender: "bot", id: "typing", text: <TypingIndicator /> },
      ]);
      const botReply = await fetchBotResponse(
        retryRequestId!,
        conversationId,
        signal
      );
      if (botReply !== null) {
        setMessages((prev: any) =>
          prev
            .filter((msg: any) => msg.id !== "typing")
            .concat({
              text: botReply.answer,
              sender: "bot",
              request_id: retryRequestId,
              sourceUrl: botReply.sourceUrl,
            })
        );
        setRetryConversationId(null);
      } else {
        handleRetryFailure(
          retryRequestId,
          retryUserMsgId,
          messages,
          conversationId
        );
      }
    } catch (error: any) {
      if (error.name === "AbortError") {
        console.log("Retry aborted as conversation changed.");
      }
    }
  };
  const handleSend = async (
    overrideMessage?: string,
    retrying: boolean = false,
    retryRequestIdParam?: string
  ) => {
    const userQuery = overrideMessage ?? input;
    if (userQuery.trim() === "") return;
    setPendingQuery(userQuery);
    setInput("");
    setIsDisabled(true);
    setChatStarted(true);
    const userMsgId = Date.now().toString();
    const userMessage = { text: userQuery, sender: "user", id: userMsgId };
    setMessages((prev: any) => [...prev, userMessage]);
    // 4. Add typing indicator (in addition to existing messages)
    const typingMessage = {
      sender: "bot",
      id: "typing",
      text: <TypingIndicator />,
    };
    setMessages((prev: any) => [...prev, typingMessage]);
    const payload =
      selectedConversation || userData
        ? {
            session_id:
              getSessionId(selectedConversation) || userData?.session_id,
            conversation_session_id: selectedConversation
              ? selectedConversation
              : userData?.conversation_session_id,
            message: userQuery,
          }
        : {
            tenant_id: TENANT_ID,
            message: userQuery,
          };
    const signal = abortControllerRef.current?.signal;
    try {
      const response = await fetch(`${API_BASE_URL}/messages/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
          "x-tenant-id": TENANT_ID || "",
        },
        body: JSON.stringify(payload),
      });
      if (response.status === 401) {
        navigate(LogoutPath);
        return;
      }
      if (!response.ok) throw new Error("Failed to send message");

      const data = await response.json();
      const { session_id, conversation_session_id, request_id } = data;
      // Refresh the side nav as soon as the new chat is created on the backend
      if (!hasSentFirstMessage.current) {
        refreshSidenav();
        hasSentFirstMessage.current = true;
      }
      setUserData((prev: any) => ({
        ...prev,
        session_id,
        conversation_session_id,
      }));
      const botReply = await fetchBotResponse(
        request_id,
        conversation_session_id,
        signal
      );
      if (botReply === null) {
        handleRetryFailure(
          request_id,
          userMsgId,
          [...messages, userMessage, typingMessage],
          conversation_session_id
        );
        return; // Do not add a bot message
      } else {
        // 5. Replace typing with bot reply, keep custom message
        setMessages((prev: any) =>
          prev
            .filter((msg: any) => msg.id !== "typing")
            .concat({ text: botReply.answer, sender: "bot", request_id: request_id, sourceUrl: botReply.sourceUrl || [], })
        );
        setRetryRequestId(null);
        setRetryUserMsgId(null);
      }
    } catch (error: any) {
      if (error.name === "AbortError") {
        console.log("Fetch aborted as conversation changed.");
        setMessages((prev: any) =>
          prev.filter((msg: any) => msg.id !== "typing")
        );
      } else {
        console.error(error);
        setMessages((prev: any) =>
          prev.filter((msg: any) => msg.id !== "typing")
        );
      }
    } finally {
      setIsDisabled(false);
    }
  };
  const fetchBotResponse = async (
    request_id: string,
    conversation_session_id?: string,
    signal?: AbortSignal
  ): Promise<{ answer: any; sourceUrl: any } | null> =>  {
    const conversationSessionId =
      selectedConversation ||
      conversation_session_id ||
      userData?.conversation_session_id;
    const url = `${API_BASE_URL}/messages/${request_id}/status?conversation_session_id=${conversationSessionId}`;

    for (let i = 0; i < POLLING_MAX_ATTEMPTS; i++) {
      // Try polling up to POLLING_MAX_ATTEMPTS times
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
            "x-tenant-id": TENANT_ID || "",
          },
          signal,
        });
        if (response.status === 401) {
          navigate(LogoutPath);
          return null;
        }
        if (!response.ok) throw new Error("Failed to fetch bot response");

        const data = await response.json();
        if (data.status === "COMPLETED") {
          setIsDisabled(false);
          if (
            data.is_title_generated_by_llm === true &&
            !hasRefreshedForTitle
          ) {
            refreshSidenav();
            setHasRefreshedForTitle(true);
          }
          return {
          answer: data.generated_response?.answer || "Server is busy, please try again later",
          sourceUrl: data.generated_response?.citations?.source_url || [], // Parse from citations.source_url
        };
        }
      } catch (error: any) {
        if (error.name === "AbortError") {
          console.log("Polling aborted.");
          throw error; // Re-throw to be caught by the calling function
        }
      }

      // Only wait if not the last iteration and response wasn't completed
      // Progressive delay: faster initial polling, then slower to reduce server load
      if (i < POLLING_MAX_ATTEMPTS - 1) {
        const delay = i < POLLING_INITIAL_PHASE_COUNT 
          ? POLLING_INITIAL_DELAY_MS 
          : POLLING_EXTENDED_DELAY_MS;
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
    await fetch(`${API_BASE_URL}/setRetry`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getAuthToken()}`,
        "x-tenant-id": TENANT_ID || "",
      },
      body: JSON.stringify({
        conversation_session_id: conversationSessionId,
        request_id,
      }),
      signal,
    });
    setIsDisabled(false);
    return null;
  };
  return (
    <>
      <div className="hid-chat-body">
        {chatLoading && <Loader variant={LoaderSizes.medium} isOverlay />}
        {messages.length === 0 ? (
          <Typography
            variant={TypographyVariant.H2}
            children={getText("WELCOME_MESSAGE")}
            className="hid-text  hid-typography__profile-header"
          />
        ) : (
          messages.map((msg: any, index: number) => {
            const isLastUserMsg =
              msg.sender === "user" && msg.id && msg.id === retryUserMsgId;
            return (
              <ChatMessage
                key={`${msg.request_id}_${index}`}
                text={msg.text}
                sender={msg.sender}
                isRequestId={msg.request_id}
                userData={userData}
                feedback={msg.feedback_type}
                isLastUserMsg={isLastUserMsg}
                retryError={!!retryUserMsgId}
                onRetry={handleRetry}
                sourceUrl={msg.sourceUrl || []}
              />
            );
          })
        )}
        <div ref={chatEndRef} />
      </div>
      <div
        className="hid-flex hid-text-input"
        onKeyDown={(e) => {
          if (e.key === "Enter" && !isdisabled) {
            e.preventDefault();
            handleSend();
          }
        }}
      >
        <TextArea
          label=""
          id="chatMessage-input"
          rows={2}
          maxLength={300}
          placeholder={getText("CHAT_INPUT_PLACEHOLDER")}
          value={input}
          onChange={(e: any) => setInput(e.target.value)}
        />
        <Button
          className="hid-input-button"
          onClick={() => handleSend()}
          disabled={isdisabled}
          icon={
            <Iconography icon="paperPlaneTop" size={IconographySizes.Medium} />
          }
        />
      </div>
    </>
  );
};

export default Chatform;
